import { memo, useMemo } from 'react';

import { PrivateWorkspaceAgentPanelMessageContentExporterMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageContentExporterMoniker';
import { PrivateWorkspaceAgentPanelMessageSkillMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageSkillMoniker';
import { PrivateWorkspaceAgentPanelReferencePillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelReferencePillChip';
import { WorkspaceAgentUnknownMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnknownMonikerPill';
import { PrivateWorkspaceAgentTableShareLinkMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/PrivateWorkspaceAgentTableShareLinkMoniker';
import { PrivateWorkspaceAgentTableShowcaseMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/PrivateWorkspaceAgentTableShowcaseMoniker';
import { type WorkspaceAgentTableMonikerSegment } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/WorkspaceAgentTableCellContent';
import { synthesizeWorkspaceAgentPillsFromContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/synthesizeWorkspaceAgentPillsFromContent';

export type PrivateWorkspaceAgentTableMonikerProps = {
  isStreaming: boolean;
  segment: WorkspaceAgentTableMonikerSegment;
};

/**
 * One moniker inside a table cell. A nested `@[table:]` stays text: another table pill would recurse
 * and add a second horizontal scroller.
 */
export const PrivateWorkspaceAgentTableMoniker = memo(
  ({ isStreaming, segment }: PrivateWorkspaceAgentTableMonikerProps) => {
    const raw = segment.type === 'pill' ? segment.raw : '';
    const pill = useMemo(
      () => (raw ? synthesizeWorkspaceAgentPillsFromContent(raw, { markArtifactHydration: true })[0] : undefined),
      [raw],
    );

    if (segment.type === 'unknownMoniker') {
      return <WorkspaceAgentUnknownMonikerPill monikerId={segment.monikerId} monikerType={segment.monikerType} />;
    }

    if (segment.entityType === 'skill') {
      return <PrivateWorkspaceAgentPanelMessageSkillMoniker skillId={segment.monikerId} />;
    }

    if (segment.entityType === 'shareLink') {
      return <PrivateWorkspaceAgentTableShareLinkMoniker shortId={segment.monikerId} />;
    }

    if (segment.entityType === 'showcase') {
      return <PrivateWorkspaceAgentTableShowcaseMoniker collectionId={segment.monikerId} />;
    }

    if (segment.entityType === 'contentExporter') {
      return <PrivateWorkspaceAgentPanelMessageContentExporterMoniker taskId={segment.monikerId} />;
    }

    if (segment.citationType === 'table' || pill?.type === 'table') {
      return <span className="break-words">{segment.raw}</span>;
    }

    return (
      <span className="inline-flex min-w-0 max-w-full">
        <PrivateWorkspaceAgentPanelReferencePillChip isStreaming={isStreaming} pill={pill} rawId={segment.monikerId} />
      </span>
    );
  },
);

PrivateWorkspaceAgentTableMoniker.displayName = 'PrivateWorkspaceAgentTableMoniker';
